import { supabase } from '../lib/supabase';
import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { icon } from '../shared/components/icons';
import { EXPLAIN } from '../shared/lib/statementLadder';   // R121: one wording for "statement vs commission (P&L)"

// ── Property Settings — THE one admin surface per property (UI/UX V8 #1) ────
// Everything admin-ish about a listing in one row: owner, property type, comp
// tier, revenue formula (with bulk assign), mgmt %, short name, dashboard
// exclusion, MTR mode. Extracted from the Admin → Properties registry and
// extended; Admin mounts this same page, so there is exactly ONE implementation.
// Every select/input saves in place — no save button, no modal.

const esc = (v: any) => Formatters.escapeHtml(String(v == null ? '' : v));
const localToday = () => new Intl.DateTimeFormat('en-CA').format(new Date());
// R119: last calendar month ('YYYY-MM') — the newest month likely to have a statement
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

export class PropertySettingsPage {
  private container: HTMLElement;
  private listings: any[] = [];
  private owners: any[] = [];
  private assignments: Record<string, string> = {};
  private types: Record<string, string> = {};
  private tiers: Record<string, number> = {};
  private formulas: any[] = [];
  private formulaByListing: Record<string, string> = {};
  private assignIdByListing: Record<string, string> = {};   // R118: the open assignment row, so 'default' can remove it
  private mtr: Record<string, any> = {};
  private mtrModes: Record<string, string> = {};   // R48: mtr_modes blob — dedicated/seasonal, absent = auto
  private priorities: Record<string, string> = {};      // R87 #2.1: A/B/C inspection priority
  private inspBudgets: Record<string, number> = {};      // R87 #2.3: per-property inspection minutes
  private goals: any = null;
  private excluded = new Set<string>();
  private search = '';
  private exception = '';   // '', 'no_owner', 'default_fee', 'mtr', 'excluded'
  private page = 0;
  private selected = new Set<string>();   // bulk-assign selection
  private bulkBusy = false;               // in-flight guard for bulk apply
  // R118: the gross formulas (GET /api/owner-finance/rules) — null when the
  // read 403s (managers/field: money is admin+director only) → no gross UI.
  // R119 (Jesse: "when I create a formula, it's not showing up as an option in
  // the per property formula picker"): they ARE the statement formulas — the
  // "Statement" picker, by name, next to the "Commission (P&L)" picker (the
  // revenue formulas). Both lists are read fresh on every render.
  private gross: { rules: any[]; assign: Record<string, string>; default_rule: string } | null = null;
  private grossBusy = new Set<string>();  // listings with a gross assign in flight
  private static PAGE = 50;

  constructor(container: HTMLElement) { this.container = container; }

  async render(): Promise<void> {
    this.container.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    await this.load();
    const focus = this.takeFocus();   // R115 guest
    this.draw();
    if (focus) this.revealRow(focus);
  }

  // R115 guest: the Calendar's listing inspector "Property Settings" hands over
  // ONE listing (one-shot sessionStorage 'ps_focus' = pms_listing_id): the
  // search narrows to that home and its row scrolls into view, highlighted.
  private takeFocus(): string | null {
    let id = '';
    try { id = sessionStorage.getItem('ps_focus') || ''; if (id) sessionStorage.removeItem('ps_focus'); } catch { return null; }
    const l = id ? this.listings.find((x: any) => String(x.pms_listing_id) === id) : null;
    if (!l) return null;
    this.search = String(l.short_name || l.name || '').toLowerCase();
    this.exception = ''; this.page = 0; this.selected.clear();
    return id;
  }
  private revealRow(id: string): void {
    const tr = this.container.querySelector<HTMLElement>(`tr[data-ps-row="${CSS.escape(id)}"]`);
    if (!tr) return;
    tr.style.background = 'var(--accent-soft)';
    tr.scrollIntoView({ block: 'center' });
  }

  private async load(): Promise<void> {
    const get = (u: string) => fetch(u).then(r => r.ok ? r.json() : null).catch(() => null);
    const [listings, owners, assigns, types, tiers, fAssigns, formulas, mtr, goals, excl, modes, prio, ibud, grossRules] = await Promise.all([
      get('/api/listings'),
      get('/api/owners'),
      get('/api/owners/assignments'),
      get('/api/listings/property-types'),
      get('/api/listings/comp-percentiles'),
      get('/api/formula-assignments'),
      get('/api/formulas'),
      get('/api/mtr-tags'),
      get('/api/goals/summary'),
      get('/api/listings/exclusions'),
      get('/api/mtr/modes'),
      get('/api/assetops/property-priority'),    // R87 #2.1
      get('/api/assetops/inspection-budgets'),   // R87 #2.3
      get('/api/owner-finance/rules'),           // R118: 403 for non-admin/director → null → no gross column
    ]);
    this.listings = (Array.isArray(listings) ? listings : [])
      .filter((l: any) => (l.status || 'active') === 'active')
      .sort((a: any, b: any) => String(a.short_name || a.name || '').localeCompare(String(b.short_name || b.name || '')));
    this.owners = Array.isArray(owners) ? owners : (owners?.owners || []);
    this.assignments = assigns?.assignments || assigns || {};
    this.types = types?.types || {};
    this.tiers = tiers?.percentiles || tiers || {};
    this.formulas = Array.isArray(formulas) ? formulas : [];
    this.formulaByListing = {};
    this.assignIdByListing = {};
    const fa = Array.isArray(fAssigns) ? fAssigns : (fAssigns?.assignments || []);
    for (const a of fa) {
      const open = a.assignment || a;
      if (open && !open.effective_to && open.formula_code) {
        const lid = String(a.pms_listing_id || a.listing_id);
        this.formulaByListing[lid] = open.formula_code;
        if (open.assignment_id != null) this.assignIdByListing[lid] = String(open.assignment_id);
      }
    }
    this.mtr = mtr || {};
    this.mtrModes = modes?.modes || {};
    this.priorities = (prio && prio.priorities) || {};
    this.inspBudgets = (ibud && ibud.budgets) || {};
    this.goals = goals;
    this.excluded = new Set(((excl && excl.excluded) || []).map(String));
    this.gross = grossRules && Array.isArray(grossRules.rules)
      ? { rules: grossRules.rules, assign: { ...(grossRules.assign || {}) }, default_rule: String(grossRules.default_rule || 'statement_default') }
      : null;
  }

  private grossName(id: string): string { const r = (this.gross?.rules || []).find((x: any) => x.id === id); return r ? String(r.name) : id; }

  // ── a row's bits that follow its formula pickers — drawn with the row and redrawn IN PLACE after a
  // save (R121 review #22/#25: the change handlers only touched the rate text, so the commission
  // formula's "Edit" kept opening the formula the row had left, and the Automatic hint stayed under a
  // row that no longer borrows from the Commission formula; no table redraw, so the select keeps focus)
  private rowName(id: string): string { const l = this.listings.find((x: any) => String(x.pms_listing_id) === String(id)); return l ? String(l.short_name || l.name || id) : String(id); }
  private defaultIsAuto(): boolean { const g = this.gross; const r = g ? g.rules.find((x: any) => x.id === g.default_rule) : null; return !!(r && r.preset === 'auto'); }
  // the commission formula's rate + its own "Edit" (P&L Review → Formulas & Fees opens that formula).
  // R121 review #23: "Edit" is for admin + director only — the same gate as the statement formula's
  // Edit / Preview / Learn beside it (this.gross = GET /owner-finance/rules, a 403 for anyone else):
  // a manager landed on the P&L lock screen, with a one-shot cf_focus left in the tab.
  private cfTail(id: string): string {
    const fee = this.formulas.find((f: any) => f.formula_code === this.formulaByListing[id]);
    if (!fee) return '';
    const rate = `<span style="font-size:10px; color:var(--text-muted);">${(Number(fee.commission_rate || 0) * 100).toFixed(1)}%</span>`;
    return rate + (this.gross ? `<button type="button" class="ps-link" data-ps-cf="${esc(fee.formula_code)}" title="Edit this commission formula" aria-label="Edit the commission formula of ${esc(this.rowName(id))}">Edit</button>` : '');
  }
  // the built-in Automatic statement formula borrows from the Commission (P&L) formula: said under a row on it
  private autoHint(id: string): string {
    if (!this.gross || this.gross.assign[id] || !this.defaultIsAuto()) return '';
    return `<div class="ps-fx-hint" data-ps-auto="${esc(id)}" title="${esc(EXPLAIN.automatic)}">Automatic copies cleaning and the commission base from the Commission (P&amp;L) formula. Learn checks it against the statements.</div>`;
  }
  private redrawCfTail(host: ParentNode, id: string): void {
    const tail = host.querySelector<HTMLElement>(`[data-ps-cf-tail="${CSS.escape(id)}"]`);
    if (!tail) return;
    tail.innerHTML = this.cfTail(id);
    this.bindCf(tail);
  }
  private redrawAutoHint(host: ParentNode, id: string): void {
    const slot = host.querySelector<HTMLElement>(`[data-ps-auto-slot="${CSS.escape(id)}"]`);
    if (slot) slot.innerHTML = this.autoHint(id);
  }
  // R121: "Edit" beside the Commission (P&L) select — Formulas & Fees opens that commission formula
  // (one-shot sessionStorage 'cf_focus'; only admin + director see the button — review #23)
  private bindCf(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>('[data-ps-cf]').forEach(a =>
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const code = a.dataset.psCf || '';
        if (!code || !this.gross) return;
        try { sessionStorage.setItem('cf_focus', code); sessionStorage.setItem('hub_tab', 'formulas'); } catch { /* private mode */ }
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'pnl' }));
      }));
  }

  private money(v: any): string { const n = Number(v); return Number.isFinite(n) ? '$' + Math.round(n).toLocaleString() : '—'; }

  // SHARED MTR-membership contract (mirror of server/statements):
  // 'never' always wins; else type==='mtr' OR forced 'always'/true OR min_nights>=28.
  private isMtr(l: any): boolean {
    const id = String(l.pms_listing_id);
    const tag = (this.mtr as any)[id];
    if (tag === 'never') return false;
    return this.types[id] === 'mtr' || tag === 'always' || tag === true || (Number(l.min_nights) || 0) >= 28;
  }

  private draw(): void {
    const host = this.container;
    const goalsById = new Map((this.goals?.items || []).map((g: any) => [String(g.id), g]));
    const pid = (l: any) => String(l.pms_listing_id);

    const noOwner = this.listings.filter(l => !this.assignments[pid(l)]);
    const defaultFee = this.listings.filter(l => !this.formulaByListing[pid(l)]);
    const mtrTagged = this.listings.filter(l => this.isMtr(l));
    const excludedN = this.listings.filter(l => this.excluded.has(pid(l)));

    const exceptions: [string, string, number][] = [
      ['no_owner', 'no owner', noOwner.length],
      ['default_fee', 'on default fee', defaultFee.length],
      ['mtr', 'MTR', mtrTagged.length],
      ['excluded', 'excluded', excludedN.length],
    ];

    let rows = this.listings.filter(l =>
      !this.search || String(l.short_name || l.name || '').toLowerCase().includes(this.search));
    if (this.exception === 'no_owner') rows = rows.filter(l => !this.assignments[pid(l)]);
    if (this.exception === 'default_fee') rows = rows.filter(l => !this.formulaByListing[pid(l)]);
    if (this.exception === 'mtr') rows = rows.filter(l => this.isMtr(l));
    if (this.exception === 'excluded') rows = rows.filter(l => this.excluded.has(pid(l)));

    const pages = Math.max(1, Math.ceil(rows.length / PropertySettingsPage.PAGE));
    if (this.page >= pages) this.page = 0;
    const pageRows = rows.slice(this.page * PropertySettingsPage.PAGE, (this.page + 1) * PropertySettingsPage.PAGE);

    const ownerOpts = (sel: string) => `<option value="">—</option>` + this.owners.map((o: any) =>
      `<option value="${esc(o.id)}" ${String(o.id) === sel ? 'selected' : ''}>${esc(o.name || 'Owner')}</option>`).join('');
    const typeOpts = (sel: string) => ['standard', 'mtr', 'owner-occupied', 'inactive'].map(t =>
      `<option value="${t}" ${t === sel ? 'selected' : ''}>${t}</option>`).join('');
    const tierOpts = (sel: number) => [25, 50, 75, 90].map(p =>
      `<option value="${p}" ${p === sel ? 'selected' : ''}>p${p}</option>`).join('');
    const formulaOpts = (sel: string) => `<option value="">default (the listing's fee)</option>` + this.formulas.map((f: any) =>
      `<option value="${esc(f.formula_code)}" title="${esc(f.formula_code)}" ${f.formula_code === sel ? 'selected' : ''}>${esc(f.formula_name || f.formula_code)}</option>`).join('');

    // R118: statement (gross) formula options — '' = back to the default (named, so it's clear which one that is)
    // R121: the built-in default reads "Automatic" — it borrows from the Commission (P&L) formula (Seagirt's case)
    const defRule = this.gross ? this.gross.rules.find((r: any) => r.id === this.gross!.default_rule) : null;
    const defAuto = !!(defRule && defRule.preset === 'auto');
    const grossOpts = (sel: string) => this.gross
      ? `<option value="">default (${esc(defAuto ? 'Automatic: borrows from Commission (P&L)' : this.grossName(this.gross.default_rule))})</option>` + this.gross.rules.map((r: any) =>
          `<option value="${esc(r.id)}" ${r.id === sel ? 'selected' : ''}>${esc(r.name)}</option>`).join('')
      : '';

    const offView = [...this.selected].filter(id => !rows.some(l => pid(l) === id)).length;
    const bulkBar = this.selected.size ? `
      <div style="display:flex; gap:10px; align-items:center; flex-wrap:wrap; background:var(--info-soft, #eff6ff); border:1px solid var(--border); border-radius:8px; padding:8px 12px; margin:0 0 10px; font-size:12.5px;">
        <strong>${this.selected.size} selected${offView ? ` (${offView} not in the current view)` : ''}</strong>
        ${this.gross ? `<span style="display:inline-flex; gap:8px; align-items:center; flex-wrap:wrap;">
          <label for="ps-bulk-gross">Assign statement formula</label>
          <select id="ps-bulk-gross" style="max-width:220px;"><option value="__pick">pick…</option>${grossOpts('__none')}</select>
          <button class="btn btn-primary btn-sm" id="ps-bulk-gross-apply" ${this.bulkBusy ? 'disabled' : ''}>${this.bulkBusy ? 'Assigning…' : `Apply to ${this.selected.size}`}</button>
        </span>` : ''}
        <span style="display:inline-flex; gap:8px; align-items:center; flex-wrap:wrap;">
          <label for="ps-bulk-formula">Assign commission formula</label>
          <select id="ps-bulk-formula">${formulaOpts('')}</select>
          <button class="btn btn-primary btn-sm" id="ps-bulk-apply" ${this.bulkBusy ? 'disabled' : ''}>${this.bulkBusy ? 'Assigning…' : `Apply to ${this.selected.size}`}</button>
        </span>
        <button class="btn btn-secondary btn-sm" id="ps-bulk-clear" ${this.bulkBusy ? 'disabled' : ''}>Clear selection</button>
      </div>` : '';

    host.innerHTML = `
      <div class="page-content">
      <style>
        /* Own copies of the registry styles — this page's primary home is the
           Portfolio hub, where AdminPage's inline styles are NOT in the DOM. */
        .ad-chip { font-size:11px; padding:4px 12px; border-radius:var(--radius-pill); cursor:pointer; border:1px solid var(--border); background:var(--card); color:var(--text-muted); transition:background .12s ease, color .12s ease, border-color .12s ease; }
        .ad-chip:hover { background:var(--bg-subtle); color:var(--text); }
        .ad-chip.active { border-color:var(--text); background:var(--text); color:#fff; }
        .ad-tbl { width:100%; border-collapse:collapse; font-size:12.5px; font-variant-numeric:tabular-nums; }
        .ad-tbl th { padding:10px 12px; text-align:left; font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; border-bottom:1px solid var(--border); white-space:nowrap; }
        .ad-tbl td { padding:9px 12px; border-bottom:1px solid var(--border-soft); white-space:nowrap; }
        .ad-tbl tbody tr:hover { background:var(--bg-subtle); }
        .ad-tbl tbody tr:last-child td { border-bottom:none; }
        .ad-tbl select { font-size:11.5px; padding:4px 8px; border:1px solid var(--border); border-radius:6px; background:var(--card); color:var(--text); max-width:130px; }
        .ad-tbl input[type="number"], .ad-tbl input:not([type]) { font-size:11.5px; padding:4px 8px; border:1px solid var(--border); border-radius:6px; background:var(--card); color:var(--text); }
        .ps-fx { display:flex; align-items:center; gap:6px; }
        .ps-fx + .ps-fx, [data-ps-auto-slot]:empty + .ps-fx { margin-top:4px; }
        .ps-fx-l { width:118px; flex:none; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); }
        .ad-tbl .ps-fx select { max-width:210px; }
        .ps-fx-links { margin:3px 0 0 124px; font-size:11px; color:var(--text-muted); }
        .ps-fx-hint { margin:2px 0 4px 124px; font-size:10.5px; line-height:1.35; color:var(--warning-text); white-space:normal; max-width:280px; }
        .ps-explain { margin:0 0 10px; padding:9px 12px; border:1px solid var(--border); border-radius:8px; background:var(--bg-subtle); font-size:12px; line-height:1.5; color:var(--text-muted); }
        .ps-explain summary { cursor:pointer; color:var(--accent); font-weight:600; min-height:28px; display:inline-flex; align-items:center; }
        .ps-explain p { margin:4px 0; }
        /* R119 review #17: row links are real buttons (keyboard + screen readers), styled as links */
        .ps-link { background:none; border:0; padding:0 1px; margin:0; font:inherit; font-size:inherit; line-height:inherit; color:var(--accent); cursor:pointer; border-radius:3px; }
        .ps-link:hover { color:var(--accent-hover); text-decoration:underline; }
        .ps-link:focus-visible { outline:none; box-shadow:var(--focus-ring); text-decoration:underline; }
        .ad-name { max-width:220px; overflow:hidden; text-overflow:ellipsis; cursor:pointer; font-weight:600; color:var(--text); }
        .ad-name:hover { color:var(--info); }
        .de-toggle-btn { padding:3px 9px; border-radius:6px; font-size:11px; cursor:pointer; border:1px solid var(--border); background:#fff; color:var(--text); transition:all .12s; }
        .de-toggle-btn:hover { border-color:#d1d5db; background:var(--bg-subtle); }
      </style>
      <div class="dash-head">
        <h1>Property Settings</h1>
        <p>The ONE place to configure a property — owner, type, comp tier, ${this.gross ? 'statement formula (what the owner portal shows), ' : ''}commission formula (the company P&amp;L) (tick rows to bulk-assign), management %, short name, and dashboard exclusion. Every change saves in place. ${this.listings.length} active properties · ${this.owners.length} owners · ${this.formulas.length} formulas. Formula/fee <em>definitions</em> live in ${this.gross ? '<a href="#" id="ps-goto-formulas" style="color:var(--info);">P&amp;L Review → Formulas &amp; Fees</a>' : 'P&amp;L Review → Formulas &amp; Fees'}.</p>
      </div>

      <div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin:6px 0 10px;">
        ${exceptions.map(([k, label, n]) =>
          `<button class="ad-chip${this.exception === k ? ' active' : ''}" data-ps-exc="${k}">${n} ${label}</button>`).join('')}
        <input id="ps-search" type="search" placeholder="Search…" value="${esc(this.search)}"
               style="margin-left:auto; font-size:12px; padding:5px 10px; border:1px solid var(--border); border-radius:6px; width:180px;">
      </div>
      ${this.gross ? `<details class="ps-explain" id="ps-fx-explain"><summary>Formulas: Statement (where Gross Revenue is built) vs Commission (P&amp;L)</summary>
        <p><strong>Statement · Gross</strong>: ${esc(EXPLAIN.statement.replace(/^Statement formula: /, ''))} ${esc(EXPLAIN.gross_where)}</p>
        <p><strong>Commission (P&amp;L)</strong>: ${esc(EXPLAIN.commission.replace(/^Commission \(P&L\) formula: /, ''))}</p>
        <p>${esc(EXPLAIN.automatic)} <strong>Edit</strong> opens the formula; <strong>Preview</strong> runs it next to the property's last statement; <strong>Learn</strong> reads its statements.</p>
      </details>` : ''}
      ${bulkBar}
      <div class="pq-card">
        <div class="u-table-wrap">
        <table class="ad-tbl">
          <thead><tr>
            <th><input type="checkbox" id="ps-sel-all" title="Select page for bulk assign" ${pageRows.length && pageRows.every(l => this.selected.has(pid(l))) ? 'checked' : ''}></th>
            <th>Property</th><th>Id</th><th>Owner</th><th>Type</th><th>Comp tier</th><th title="Statement formula = the owner's statement ladder, where Gross Revenue is built (what the owner portal shows). Commission (P&amp;L) formula = how the company books each reservation in its own P&amp;L. Both are defined in P&amp;L Review → Formulas &amp; Fees.">Formulas</th>
            <th>Mgmt %</th><th>Short name</th><th title="Excluded listings disappear from every dashboard number">Excl.</th>
            <th title="Inspection priority — with limited resources, A properties come first in the queue">Prio</th><th title="Expected inspection minutes (blank = size heuristic)">Insp. ⏱</th><th>MTR</th>
            <th style="text-align:right;">Annual goal</th><th>⋯</th>
          </tr></thead>
          <tbody>
            ${pageRows.map(l => {
              const id = pid(l);
              const g: any = goalsById.get(id);
              const tag = (this.mtr as any)[id];
              const mtrMode = tag === 'never' ? 'never' : (tag === 'always' || tag === true) ? 'always' : 'auto';
              const autoResolves = this.types[id] === 'mtr' || (Number(l.min_nights) || 0) >= 28 ? 'mtr' : 'standard';
              // R48: dedicated (MTR-only) vs seasonal (STR trying for MTR) — only meaningful when the property IS mtr
              const inMtrSet = this.isMtr(l);
              const modeVal = this.mtrModes[id] || 'auto';
              return `<tr data-ps-row="${esc(id)}">
                <td><input type="checkbox" data-ps-sel="${id}" ${this.selected.has(id) ? 'checked' : ''}></td>
                <td class="ad-name" data-ps-open="${id}" title="Open in Revenue Manager">${esc(l.short_name || l.name || id)}</td>
                <td class="mono num" style="color:var(--text-muted);">${id}</td>
                <td><select data-ps-owner="${id}">${ownerOpts(this.assignments[id] || '')}</select></td>
                <td><select data-ps-type="${id}">${typeOpts(this.types[id] || 'standard')}</select></td>
                <td><select data-ps-tier="${id}">${tierOpts(this.tiers[id] || 50)}</select></td>
                <td>${this.gross ? `<div class="ps-fx"><label class="ps-fx-l" for="ps-gross-${esc(id)}" title="${esc(`${EXPLAIN.statement} ${EXPLAIN.gross_where}`)}">Statement · Gross</label><select id="ps-gross-${esc(id)}" data-ps-gross="${id}" ${this.grossBusy.has(id) ? 'disabled' : ''} title="Statement formula: the owner's statement ladder, where Gross Revenue is built (what the owner portal shows)">${grossOpts(this.gross.assign[id] || '')}</select><button type="button" class="ps-link" data-ps-sf="edit" data-ps-sf-lid="${esc(id)}" title="Edit this property's statement formula" aria-label="Edit the statement formula of ${esc(l.short_name || l.name || id)}">Edit</button></div>
                    <div data-ps-auto-slot="${esc(id)}">${this.autoHint(id)}</div>` : ''}
                    <div class="ps-fx"><label class="ps-fx-l" for="ps-formula-${esc(id)}" title="${esc(EXPLAIN.commission)}">Commission (P&amp;L)</label><select id="ps-formula-${esc(id)}" data-ps-formula="${id}" title="Commission formula: how the company books each reservation in its own P&amp;L (default = the listing's fee)">${formulaOpts(this.formulaByListing[id] || '')}</select><span data-ps-cf-tail="${esc(id)}" style="display:inline-flex; gap:6px; align-items:center;">${this.cfTail(id)}</span></div>
                    ${this.gross ? `<div class="ps-fx-links"><button type="button" class="ps-link" data-ps-sf="preview" data-ps-sf-lid="${esc(id)}" title="Open its statement formula on this property's last month, next to the statement" aria-label="Preview the statement formula of ${esc(l.short_name || l.name || id)}">Preview</button> · <button type="button" class="ps-link" data-ps-sf="learn" data-ps-sf-lid="${esc(id)}" title="Learn this property's statement formula and facts from its statements" aria-label="Learn the statement formula of ${esc(l.short_name || l.name || id)}">Learn</button></div>` : ''}</td>
                <td><input type="number" min="0" max="100" step="0.5" data-ps-mgmt="${id}" value="${l.mgmt_fee_percent ?? ''}" placeholder="—" style="width:58px;" title="Management fee % (blank = formula/default)"></td>
                <td><input data-ps-short="${id}" value="${esc(l.short_name || '')}" placeholder="${esc((l.name || '').slice(0, 18))}" style="width:110px;" title="Short display name used across the app"></td>
                <td style="text-align:center;"><input type="checkbox" data-ps-excl="${id}" ${this.excluded.has(id) ? 'checked' : ''} title="Exclude from every dashboard number"></td>
                <td><select data-ps-prio="${id}" style="max-width:56px;" title="Inspection priority — A first when resources are tight"><option value="">—</option>${['A','B','C'].map(pr => `<option value="${pr}" ${this.priorities[id] === pr ? 'selected' : ''}>${pr}</option>`).join('')}</select></td>
                <td><input type="number" min="5" max="480" step="5" data-ps-ibud="${id}" value="${this.inspBudgets[id] ?? ''}" placeholder="auto" style="width:56px;" title="Expected inspection minutes for this home (blank = size heuristic)"></td>
                <td><select data-ps-mtr="${id}" style="max-width:88px;">
                      <option value="auto" ${mtrMode === 'auto' ? 'selected' : ''}>auto</option>
                      <option value="always" ${mtrMode === 'always' ? 'selected' : ''}>always</option>
                      <option value="never" ${mtrMode === 'never' ? 'selected' : ''}>never</option>
                    </select>
                    <span data-ps-mtr-hint="${id}" style="font-size:10px; color:var(--text-muted); display:${mtrMode === 'auto' ? 'inline' : 'none'};">auto → ${autoResolves}</span>
                    <select data-ps-mtrmode="${id}" style="max-width:88px; margin-top:3px;" ${inMtrSet ? '' : 'disabled'} title="MTR mode — dedicated = MTR-only board, seasonal = STR trying for MTR; auto → dedicated when type is 'mtr', else seasonal${inMtrSet ? '' : ' (not an MTR property)'}">
                      <option value="auto" ${modeVal === 'auto' ? 'selected' : ''}>mode: auto</option>
                      <option value="dedicated" ${modeVal === 'dedicated' ? 'selected' : ''}>dedicated</option>
                      <option value="seasonal" ${modeVal === 'seasonal' ? 'selected' : ''}>seasonal</option>
                    </select></td>
                <td class="mono num" style="text-align:right;">${g?.goal ? this.money(g.goal) : '—'}</td>
                <td><button type="button" class="ps-link" data-ps-quote="${id}" style="font-size:11px;" aria-label="Quote ${esc(l.short_name || l.name || id)}">Quote…</button></td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
        </div>
        ${pages > 1 ? `<div style="display:flex; gap:8px; justify-content:flex-end; padding:8px 14px; font-size:12px;">
          <button class="de-toggle-btn" id="ps-prev" ${this.page === 0 ? 'disabled' : ''}>‹</button>
          <span style="align-self:center; color:var(--text-muted);">${this.page + 1}/${pages}</span>
          <button class="de-toggle-btn" id="ps-next" ${this.page >= pages - 1 ? 'disabled' : ''}>›</button>
        </div>` : ''}
      </div>
      </div>`;
    this.bind(pageRows.map(pid));
  }

  private async post(url: string, body: any): Promise<boolean> {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => null);
    return !!(r && r.ok);
  }

  // R118: post() that also hands back the reply — the formula assign needs the
  // new assignment_id and the server's reason when it refuses.
  private async send(method: string, url: string, body?: any): Promise<{ ok: boolean; data: any; error: string }> {
    try {
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
      const data = await r.json().catch(() => ({}));
      return { ok: r.ok, data, error: r.ok ? '' : String((data && data.error) || `HTTP ${r.status}`) };
    } catch { return { ok: false, data: null, error: 'could not reach the server' }; }
  }

  // R118: assign a formula to one property (code '' = back to the default fee:
  // the open assignment is removed on the server, not just in this view — it
  // used to come back on reload). Returns '' on success, else the reason.
  private async saveFormula(id: string, code: string): Promise<string> {
    if (code) {
      const r = await this.send('POST', '/api/formula-assignments', { listing_id: id, formula_code: code, effective_from: localToday() });
      if (!r.ok) return r.error;
      this.formulaByListing[id] = code;
      if (r.data && r.data.assignment_id != null) this.assignIdByListing[id] = String(r.data.assignment_id);
      else delete this.assignIdByListing[id];
      return '';
    }
    let aid = this.assignIdByListing[id];
    if (!aid) {   // assigned somewhere else since this page loaded → look the open row up
      const r = await this.send('GET', '/api/formula-assignments');
      if (!r.ok || !Array.isArray(r.data)) return r.error || 'could not look up the current assignment';
      const row = r.data.find((x: any) => String(x.pms_listing_id || x.listing_id) === id);
      aid = row && row.assignment && row.assignment.assignment_id != null ? String(row.assignment.assignment_id) : '';
    }
    if (aid) {
      const r = await this.send('DELETE', `/api/formula-assignments/${encodeURIComponent(aid)}`);
      if (!r.ok) return r.error;
    }
    delete this.formulaByListing[id];
    delete this.assignIdByListing[id];
    return '';
  }

  // R118: POST /api/owner-finance/assign { listing_ids, rule_id } (null = back
  // to the default). Adopts the server's assign map; '' or the error message.
  private async assignGross(ids: string[], ruleId: string | null): Promise<string> {
    try {
      const r = await fetch('/api/owner-finance/assign', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listing_ids: ids, rule_id: ruleId }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return j.error || `HTTP ${r.status}`;
      if (this.gross) {
        if (j && j.assign && typeof j.assign === 'object') this.gross.assign = { ...j.assign };
        else for (const id of ids) { if (ruleId) this.gross.assign[id] = ruleId; else delete this.gross.assign[id]; }
      }
      return '';
    } catch (e: any) { return (e && e.message) || 'network error'; }
  }

  private toast(msg: string): void {
    const el = document.createElement('div');
    el.className = 'sup-toast';
    el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--text,#0f172a);color:#fff;padding:9px 16px;border-radius:8px;font-size:13px;z-index:5000;';
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2200);
  }

  private bind(pageIds: string[]): void {
    const host = this.container;
    const q = <T extends Element>(s: string) => host.querySelector<T>(s);

    host.querySelectorAll<HTMLElement>('[data-ps-exc]').forEach(b =>
      b.addEventListener('click', () => {
        const k = b.dataset.psExc!;
        this.exception = this.exception === k ? '' : k;
        this.page = 0;
        this.selected.clear();   // ticks follow the visible view — no hidden carryover into bulk apply
        this.draw();
      }));
    q<HTMLInputElement>('#ps-search')?.addEventListener('input', (e) => {
      this.search = (e.target as HTMLInputElement).value.toLowerCase();
      this.page = 0;
      this.draw();
      const inp = q<HTMLInputElement>('#ps-search');
      if (inp) { inp.focus(); inp.setSelectionRange(inp.value.length, inp.value.length); }
    });
    q('#ps-prev')?.addEventListener('click', () => { this.page--; this.draw(); });
    q('#ps-next')?.addEventListener('click', () => { this.page++; this.draw(); });

    // bulk selection
    q<HTMLInputElement>('#ps-sel-all')?.addEventListener('change', (e) => {
      const on = (e.target as HTMLInputElement).checked;
      pageIds.forEach(id => on ? this.selected.add(id) : this.selected.delete(id));
      this.draw();
    });
    host.querySelectorAll<HTMLInputElement>('[data-ps-sel]').forEach(cb =>
      cb.addEventListener('change', () => {
        cb.checked ? this.selected.add(cb.dataset.psSel!) : this.selected.delete(cb.dataset.psSel!);
        this.draw();
      }));
    q('#ps-bulk-clear')?.addEventListener('click', () => { this.selected.clear(); this.draw(); });
    q('#ps-bulk-apply')?.addEventListener('click', async () => {
      if (this.bulkBusy) return;   // double-click would interleave two loops → duplicate open assignments
      const code = q<HTMLSelectElement>('#ps-bulk-formula')?.value || '';
      if (!code) { this.toast('Pick a commission formula to bulk-assign'); return; }
      this.bulkBusy = true;
      this.draw();   // button shows "Assigning…" disabled
      const ids = [...this.selected];
      let ok = 0;
      try {
        for (const id of ids) {
          if (!(await this.saveFormula(id, code))) ok++;   // R118: keeps the new assignment_id too
        }
      } finally {
        this.bulkBusy = false;
        this.selected.clear();
        this.draw();
      }
      const fname = (this.formulas.find((f: any) => f.formula_code === code) || {}).formula_name || code;
      this.toast(`Commission formula "${fname}" assigned to ${ok} of ${ids.length} properties`);
    });

    // inline saves — same endpoints the old registry used
    host.querySelectorAll<HTMLSelectElement>('[data-ps-owner]').forEach(s =>
      s.addEventListener('change', () => {
        this.assignments[s.dataset.psOwner!] = s.value;
        this.post('/api/listings/owner', { listing_id: s.dataset.psOwner, owner_id: s.value || null });
      }));
    host.querySelectorAll<HTMLSelectElement>('[data-ps-type]').forEach(s =>
      s.addEventListener('change', () => {
        this.types[s.dataset.psType!] = s.value;
        this.post('/api/listings/property-type', { id: s.dataset.psType, type: s.value });
      }));
    host.querySelectorAll<HTMLSelectElement>('[data-ps-prio]').forEach(s =>
      s.addEventListener('change', () => {
        const id = s.dataset.psPrio!;
        if (s.value) this.priorities[id] = s.value; else delete this.priorities[id];
        this.post('/api/assetops/property-priority', { listing_id: id, priority: s.value || null });
      }));
    host.querySelectorAll<HTMLInputElement>('[data-ps-ibud]').forEach(inp =>
      inp.addEventListener('change', () => {
        const id = inp.dataset.psIbud!;
        const v = inp.value === '' ? null : Number(inp.value);
        if (v != null && !(v >= 5 && v <= 480)) {   // review #38: mirror the server's range, don't cache a value that 400s
          alert('Inspection minutes must be 5–480 (blank clears back to the size heuristic).');
          inp.value = this.inspBudgets[id] != null ? String(this.inspBudgets[id]) : '';
          return;
        }
        if (v != null) this.inspBudgets[id] = v; else delete this.inspBudgets[id];
        this.post('/api/assetops/inspection-budgets', { listing_id: id, minutes: inp.value === '' ? null : v });
      }));
    host.querySelectorAll<HTMLSelectElement>('[data-ps-tier]').forEach(s =>
      s.addEventListener('change', () => {
        this.tiers[s.dataset.psTier!] = parseInt(s.value, 10);
        this.post('/api/listings/comp-percentile', { id: s.dataset.psTier, percentile: parseInt(s.value, 10) });
      }));
    // R118: the result is checked — a refused assign used to look saved until
    // reload — and 'default' removes the server assignment. On failure the
    // select goes back to what the server still has, and says why.
    host.querySelectorAll<HTMLSelectElement>('[data-ps-formula]').forEach(s =>
      s.addEventListener('change', async () => {
        const id = s.dataset.psFormula!;
        const prev = this.formulaByListing[id] || '';
        const code = s.value;
        s.disabled = true;
        const err = await this.saveFormula(id, code);
        s.disabled = false;
        if (err) {
          s.value = prev;
          this.toast(`Formula not saved: ${err}`);
          return;
        }
        const fee = this.formulas.find((f: any) => f.formula_code === code);
        this.redrawCfTail(host, id);   // R121 review #22: the rate AND the "Edit" follow the formula now assigned
        this.toast(code ? `Commission formula “${fee ? (fee.formula_name || code) : code}” assigned` : 'Back on the default fee');
      }));
    // R118: gross formula per property — POST /api/owner-finance/assign patches
    // ONLY that listing's assignment (no store version to race); a failure
    // puts the select back and says why.
    host.querySelectorAll<HTMLSelectElement>('[data-ps-gross]').forEach(s =>
      s.addEventListener('change', async () => {
        const id = s.dataset.psGross!;
        if (!this.gross || this.grossBusy.has(id)) return;
        const prev = this.gross.assign[id] || '';
        const next = s.value;
        this.grossBusy.add(id); s.disabled = true;
        const err = await this.assignGross([id], next || null);
        this.grossBusy.delete(id);
        // the table may have redrawn meanwhile (search / paging) — act on the live select
        const cur = host.querySelector<HTMLSelectElement>(`[data-ps-gross="${CSS.escape(id)}"]`) || s;
        cur.disabled = false;
        if (err) { cur.value = prev; this.toast(`Statement formula not saved — ${err}`); return; }
        cur.value = this.gross.assign[id] || '';
        this.redrawAutoHint(host, id);   // R121 review #25: the Automatic hint follows the formula now assigned
        this.toast(`Statement formula: ${next ? this.grossName(next) : `default (${this.grossName(this.gross.default_rule)})`}`);
      }));
    q('#ps-bulk-gross-apply')?.addEventListener('click', async () => {
      if (this.bulkBusy || !this.gross) return;
      const v = q<HTMLSelectElement>('#ps-bulk-gross')?.value ?? '__pick';
      if (v === '__pick') { this.toast('Pick a statement formula to bulk-assign'); return; }
      const ids = [...this.selected];
      this.bulkBusy = true;
      this.draw();
      let err = '';
      try { err = await this.assignGross(ids, v || null); }
      finally {
        this.bulkBusy = false;
        if (!err) this.selected.clear();   // a failure keeps the ticks for a retry
        this.draw();
      }
      this.toast(err ? `Statement formula not assigned — ${err}` : `Statement formula "${v ? this.grossName(v) : 'default'}" assigned to ${ids.length} propert${ids.length === 1 ? 'y' : 'ies'}`);
    });
    q('#ps-goto-formulas')?.addEventListener('click', (e) => {
      e.preventDefault();
      try { sessionStorage.setItem('hub_tab', 'formulas'); } catch { /* private mode */ }
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'pnl' }));
    });
    // R119: "Preview · Learn" — hand THIS property to Formulas & Fees (one-shot
    // sessionStorage 'sf_focus'), which opens the statement formula builder on
    // the formula it uses, previewing last month against its statement
    // (Learn also reads its statements and proposes the formula + facts).
    host.querySelectorAll<HTMLElement>('[data-ps-sf]').forEach(a =>
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const lid = a.dataset.psSfLid || '';
        if (!lid) return;
        try {
          sessionStorage.setItem('sf_focus', JSON.stringify({ listing_id: lid, month: lastMonth(), ...(a.dataset.psSf === 'learn' ? { learn: true } : {}), ...(a.dataset.psSf === 'edit' ? { mode: 'edit' } : {}) }));
          sessionStorage.setItem('hub_tab', 'formulas');
        } catch { /* private mode */ }
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'pnl' }));
      }));
    this.bindCf(host);
    host.querySelectorAll<HTMLSelectElement>('[data-ps-mtr]').forEach(sel =>
      sel.addEventListener('change', async () => {
        const id = sel.dataset.psMtr!;
        const mode = sel.value;
        if (mode === 'auto') delete (this.mtr as any)[id];
        else (this.mtr as any)[id] = mode;
        const hint = host.querySelector<HTMLElement>(`[data-ps-mtr-hint="${id}"]`);
        if (hint) hint.style.display = mode === 'auto' ? 'inline' : 'none';
        // R48: membership change flips whether the mode select is meaningful
        const modeSel = host.querySelector<HTMLSelectElement>(`[data-ps-mtrmode="${id}"]`);
        if (modeSel) { const l = this.listings.find(x => String(x.pms_listing_id) === id); modeSel.disabled = !l || !this.isMtr(l); }
        await this.post('/api/mtr-tags', { listing_id: id, mode });
      }));
    // R48: MTR mode — dedicated vs seasonal split on the MTR board ('auto' deletes the key)
    host.querySelectorAll<HTMLSelectElement>('[data-ps-mtrmode]').forEach(sel =>
      sel.addEventListener('change', async () => {
        const id = sel.dataset.psMtrmode!;
        if (sel.value === 'auto') delete this.mtrModes[id]; else this.mtrModes[id] = sel.value;
        await this.post('/api/mtr/modes', { listing_id: id, mode: sel.value });
      }));

    // mgmt % + short name — same direct-supabase write the Listings edit modal uses
    const saveListingField = async (id: string, patch: Record<string, any>, label: string) => {
      const { error } = await supabase.from('listings').update(patch).eq('pms_listing_id', id);
      if (error) this.toast(`${label} save failed: ${error.message}`);
      else {
        const l = this.listings.find(x => String(x.pms_listing_id) === id);
        if (l) Object.assign(l, patch);
        this.toast(`${label} saved`);
      }
    };
    host.querySelectorAll<HTMLInputElement>('[data-ps-mgmt]').forEach(inp =>
      inp.addEventListener('change', () => {
        const v = inp.value.trim();
        saveListingField(inp.dataset.psMgmt!, { mgmt_fee_percent: v === '' ? null : Number(v) }, 'Mgmt %');
      }));
    host.querySelectorAll<HTMLInputElement>('[data-ps-short]').forEach(inp =>
      inp.addEventListener('change', () => {
        saveListingField(inp.dataset.psShort!, { short_name: inp.value.trim() || null }, 'Short name');
      }));

    host.querySelectorAll<HTMLInputElement>('[data-ps-excl]').forEach(cb =>
      cb.addEventListener('change', async () => {
        const id = cb.dataset.psExcl!;
        const on = cb.checked;
        const ok = await this.post('/api/listings/exclusions', { id, excluded: on });
        if (ok) {
          on ? this.excluded.add(id) : this.excluded.delete(id);
          this.toast(on ? 'Excluded from all dashboards' : 'Included again');
        } else { cb.checked = !on; this.toast('Exclusion save failed'); }
      }));

    host.querySelectorAll<HTMLElement>('[data-ps-open]').forEach(el =>
      el.addEventListener('click', () => {
        sessionStorage.setItem('workup_preselect', el.dataset.psOpen!);
        sessionStorage.setItem('workup_tab', 'propensity');
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'data-explorer' }));
      }));
    host.querySelectorAll<HTMLElement>('[data-ps-quote]').forEach(el =>
      el.addEventListener('click', () => {
        sessionStorage.setItem('quote_preselect', el.dataset.psQuote!);
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'booking-quote' }));
      }));
  }
}
